import { useEffect, useState } from "react";
import { FileText, Loader2, Sparkles } from "lucide-react";
import { MdOut } from "./MdOut";

const DEMO_TOPIC = "Why remote work boosts productivity";
const DEMO_OUTPUT = `## Why Remote Work Boosts Productivity

Remote work isn't just a trend — it's a **productivity revolution**. Studies show remote workers are 13% more productive than their office counterparts.

### Key Benefits
- **Zero commute** = 2 extra hours per day
- **Fewer distractions** from office noise
- **Flexible schedules** aligned with peak energy
- **Better work-life balance** reduces burnout

> "Companies that embrace remote work see 25% lower turnover rates."

The future of work is here. Are you ready?`;

export function InteractiveDemo() {
  const [step, setStep] = useState(0);
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (step !== 0) return undefined;
    let i = 0;
    const t = setInterval(() => {
      if (i <= DEMO_TOPIC.length) {
        setTyped(DEMO_TOPIC.substring(0, i));
        i += 1;
      } else {
        clearInterval(t);
        setTimeout(() => setStep(1), 500);
      }
    }, 50);
    return () => clearInterval(t);
  }, [step]);

  useEffect(() => {
    if (step !== 1) return undefined;
    const t = setTimeout(() => setStep(2), 1500);
    return () => clearTimeout(t);
  }, [step]);

  return (
    <div className="bg-white border border-zinc-200 shadow-xl shadow-zinc-200/50 overflow-hidden max-w-3xl mx-auto" data-testid="interactive-demo">
      <div className="flex items-center gap-2 border-b border-zinc-200 px-4 py-3 bg-zinc-50">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-zinc-300" />
          <div className="w-3 h-3 rounded-full bg-zinc-300" />
          <div className="w-3 h-3 rounded-full bg-zinc-300" />
        </div>
        <span className="text-xs text-zinc-400 font-medium ml-2">CopyForge AI — Live Demo</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-zinc-200">
        <div className="p-5 space-y-3">
          <div>
            <span className="text-xs font-bold tracking-wider text-zinc-400 uppercase">Template</span>
            <div className="mt-1 flex items-center gap-2 p-2 border border-brand bg-brand/5">
              <FileText className="w-4 h-4 text-brand" />
              <span className="text-sm font-semibold text-zinc-900">Blog Post</span>
            </div>
          </div>
          <div>
            <span className="text-xs font-bold tracking-wider text-zinc-400 uppercase">Topic</span>
            <div className="mt-1 bg-zinc-50 border border-zinc-200 px-3 py-2 text-sm text-zinc-900 min-h-[40px]">
              {typed}
              <span className={step === 0 ? "animate-blink text-brand" : "hidden"}>|</span>
            </div>
          </div>
          <div className={`transition-all duration-300 ${step >= 1 ? "opacity-100" : "opacity-0"}`}>
            <button className="w-full bg-brand text-white font-semibold py-2.5 flex items-center justify-center gap-2 text-sm">
              {step === 1 ? (<><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>) : (<><Sparkles className="w-4 h-4" /> Generate (2 cr)</>)}
            </button>
          </div>
        </div>
        <div className="p-5 bg-zinc-50/50 min-h-[250px]">
          {step < 2 ? (
            <div className="flex flex-col items-center justify-center h-full text-zinc-300">
              {step === 1 ? (
                <>
                  <Loader2 className="w-8 h-8 animate-spin text-brand mb-2" />
                  <p className="text-xs text-zinc-400">Writing...</p>
                </>
              ) : (
                <>
                  <Sparkles className="w-8 h-8 mb-2 opacity-30" />
                  <p className="text-xs">Output appears here</p>
                </>
              )}
            </div>
          ) : (
            <div className="animate-fade-in text-xs leading-relaxed overflow-hidden max-h-[220px]">
              <MdOut content={DEMO_OUTPUT} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
